import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Chip, Grid, TextField, IconButton, Tooltip,
  InputAdornment, Divider, LinearProgress,
} from "@mui/material";
import PageLoader from "../../Components/Loader/PageLoader";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SaveIcon from "@mui/icons-material/Save";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface LineItem {
  product_id: number; product_name: string; ordered_qty: number;
  already_received: number; already_damaged: number; remaining: number;
  unit: string; price: number;
}

interface POData {
  po_id: number; po_number: string; supplier_name: string; order_date: string;
  due_date: string; status: string; receiving_status: string;
  items: LineItem[];
  summary: { total_ordered: number; total_received: number; total_remaining: number };
}

interface ReceiveEntry { received_qty: number; damaged_qty: number; }

const thSx = { fontWeight: 600, fontSize: 10, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fafbfc" };

const statusColors: Record<string, { bg: string; color: string; label: string }> = {
  pending: { bg: "#f1f5f9", color: "#64748b", label: "Pending" },
  partial: { bg: "#fff7ed", color: "#ea580c", label: "Partially Received" },
  completed: { bg: "#f0fdf4", color: "#16a34a", label: "Completed" },
};

const ReceiveGoods: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [poData, setPoData] = useState<POData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState<Record<number, ReceiveEntry>>({});
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadPO = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_URL}/purchase-order/${id}/receive`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) {
        setPoData(json.data);
        // Initialize entries
        const init: Record<number, ReceiveEntry> = {};
        json.data.items.forEach((item: LineItem) => { init[item.product_id] = { received_qty: 0, damaged_qty: 0 }; });
        setEntries(init);
      } else setError(json.message || "Failed to load PO");
    } catch { setError("Failed to load purchase order"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPO(); }, [id]);

  const updateEntry = (productId: number, field: keyof ReceiveEntry, value: number) => {
    setEntries(prev => ({ ...prev, [productId]: { ...prev[productId], [field]: Math.max(0, value) } }));
  };

  const receiveAllRemaining = () => {
    if (!poData) return;
    const updated: Record<number, ReceiveEntry> = {};
    poData.items.forEach(item => {
      updated[item.product_id] = { received_qty: item.remaining, damaged_qty: 0 };
    });
    setEntries(updated);
  };

  const hasAnyQty = useMemo(() => Object.values(entries).some(e => e.received_qty > 0), [entries]);

  const validationErrors = useMemo(() => {
    if (!poData) return {};
    const errs: Record<number, string> = {};
    poData.items.forEach(item => {
      const entry = entries[item.product_id];
      if (!entry) return;
      if (entry.received_qty > item.remaining) errs[item.product_id] = `Cannot exceed remaining (${item.remaining})`;
      if (entry.damaged_qty > entry.received_qty) errs[item.product_id] = "Damaged cannot exceed received";
    });
    return errs;
  }, [entries, poData]);

  const hasErrors = Object.keys(validationErrors).length > 0;

  const handleSubmit = async () => {
    if (!poData || !hasAnyQty || hasErrors) return;
    setSaving(true); setError(""); setSuccessMsg("");
    try {
      const items = Object.entries(entries)
        .filter(([_, e]) => e.received_qty > 0)
        .map(([pid, e]) => ({ product_id: Number(pid), received_qty: e.received_qty, damaged_qty: e.damaged_qty }));

      const res = await fetch(`${API_URL}/goods-receipt`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ po_id: Number(id), items }),
      });
      const json = await res.json();
      if (json.success) {
        setSuccessMsg(json.message || "Goods received successfully");
        loadPO(); // Reload to update quantities
      } else setError(json.message || "Failed to save");
    } catch { setError("Something went wrong"); }
    finally { setSaving(false); }
  };

  const receivePct = poData ? (poData.summary.total_ordered > 0 ? Math.round((poData.summary.total_received / poData.summary.total_ordered) * 100) : 0) : 0;
  const isCompleted = poData?.receiving_status === "completed";

  if (loading) return <PageLoader height="60vh" />;

  if (error && !poData) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: "1px solid #fecaca", bgcolor: "#fef2f2", textAlign: "center" }}>
        <Typography fontSize={16} fontWeight={700} color="#dc2626">{error}</Typography>
      </Paper>
    </Box>
  );

  if (!poData) return null;
  const sc = statusColors[poData.receiving_status] || statusColors.pending;

  return (
    <Box sx={{ bgcolor: "#f8f9fb", minHeight: "100vh" }}>
      {/* Top Bar */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 1.5, position: "sticky", top: 0, zIndex: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{ border: "1px solid #e2e8f0", borderRadius: 2, width: 36, height: 36 }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: "linear-gradient(135deg, #1e293b, #334155)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LocalShippingIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={17} fontWeight={700} color="#0f172a">Receive Goods</Typography>
            <Typography fontSize={11} color="#94a3b8">{poData.po_number}</Typography>
          </Box>
          <Chip label={sc.label} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: sc.bg, color: sc.color }} />
          {!isCompleted && (
            <>
              <Button size="small" variant="outlined" onClick={receiveAllRemaining}
                startIcon={<PlaylistAddCheckIcon sx={{ fontSize: 14 }} />}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>
                Receive All
              </Button>
              <Button size="small" variant="contained" disableElevation onClick={handleSubmit} disabled={!hasAnyQty || hasErrors || saving}
                startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#0f172a", px: 3, "&:hover": { bgcolor: "#1e293b" }, "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                {saving ? "Saving..." : "Confirm Receipt"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 3, width: "100%" }}>
        {/* Success/Error messages */}
        {successMsg && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2.5, border: "1px solid #bbf7d0", bgcolor: "#f0fdf4", display: "flex", alignItems: "center", gap: 1.5 }}>
            <CheckCircleIcon sx={{ fontSize: 20, color: "#16a34a" }} />
            <Typography fontSize={13} fontWeight={600} color="#16a34a">{successMsg}</Typography>
          </Paper>
        )}
        {error && poData && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2.5, border: "1px solid #fecaca", bgcolor: "#fef2f2", display: "flex", alignItems: "center", gap: 1.5 }}>
            <WarningAmberIcon sx={{ fontSize: 20, color: "#dc2626" }} />
            <Typography fontSize={13} fontWeight={600} color="#dc2626">{error}</Typography>
          </Paper>
        )}

        {/* PO Header */}
        <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <Box sx={{ height: 4, background: "linear-gradient(90deg, #0f172a, #334155, #00838f)" }} />
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>Supplier</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", mt: 0.25 }}>{poData.supplier_name || "—"}</Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>PO Number</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", mt: 0.25, fontVariantNumeric: "tabular-nums" }}>{poData.po_number}</Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>Order Date</Typography>
                <Typography sx={{ fontSize: 13, color: "#334155", mt: 0.25 }}>{poData.order_date ? new Date(poData.order_date).toLocaleDateString() : "—"}</Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>Receiving Progress</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress variant="determinate" value={receivePct}
                      sx={{ height: 8, borderRadius: 4, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { bgcolor: receivePct >= 100 ? "#16a34a" : "#00838f", borderRadius: 4 } }} />
                  </Box>
                  <Typography fontSize={13} fontWeight={700} color={receivePct >= 100 ? "#16a34a" : "#0f172a"}>{receivePct}%</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: "Total Ordered", value: poData.summary.total_ordered, gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)" },
            { label: "Total Received", value: poData.summary.total_received, gradient: "linear-gradient(135deg, #16a34a, #15803d)" },
            { label: "Remaining", value: poData.summary.total_remaining, gradient: poData.summary.total_remaining > 0 ? "linear-gradient(135deg, #ea580c, #c2410c)" : "linear-gradient(135deg, #16a34a, #15803d)" },
          ].map((c, i) => (
            <Grid item xs={4} key={i}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: c.gradient, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography fontSize={18} fontWeight={800} color="#fff">{c.value}</Typography>
                </Box>
                <Typography fontSize={11} color="#64748b" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{c.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Items Table */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Product</TableCell>
                  <TableCell sx={thSx} align="center">Unit</TableCell>
                  <TableCell sx={thSx} align="right">Ordered</TableCell>
                  <TableCell sx={thSx} align="right">Received</TableCell>
                  <TableCell sx={thSx} align="right">Remaining</TableCell>
                  {!isCompleted && <TableCell sx={thSx} align="center">Receiving Now</TableCell>}
                  {!isCompleted && <TableCell sx={thSx} align="center">Damaged</TableCell>}
                  <TableCell sx={thSx} align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {poData.items.map(item => {
                  const entry = entries[item.product_id] || { received_qty: 0, damaged_qty: 0 };
                  const hasErr = validationErrors[item.product_id];
                  const isDone = item.remaining === 0;

                  return (
                    <TableRow key={item.product_id} sx={{ bgcolor: isDone ? "#fafbfc" : "inherit", "&:hover": { bgcolor: "#fafbfe" } }}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography fontSize={13} fontWeight={600} color="#1e293b" sx={{ textTransform: "capitalize" }}>{item.product_name}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Typography fontSize={11} fontWeight={600} color="#64748b" sx={{ bgcolor: "#f1f5f9", display: "inline-block", px: 1, py: 0.25, borderRadius: 1 }}>{item.unit}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Typography fontSize={14} fontWeight={700} color="#1e293b" sx={{ fontVariantNumeric: "tabular-nums" }}>{item.ordered_qty}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Typography fontSize={14} fontWeight={700} color={item.already_received > 0 ? "#16a34a" : "#cbd5e1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {item.already_received}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Typography fontSize={14} fontWeight={700} color={item.remaining > 0 ? "#ea580c" : "#16a34a"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {item.remaining}
                        </Typography>
                      </TableCell>
                      {!isCompleted && (
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          {isDone ? (
                            <Typography fontSize={11} color="#16a34a" fontWeight={600}>Done</Typography>
                          ) : (
                            <TextField size="small" type="number" value={entry.received_qty || ""} placeholder="0"
                              onChange={e => updateEntry(item.product_id, "received_qty", Number(e.target.value))}
                              error={!!hasErr}
                              helperText={hasErr || ""}
                              inputProps={{ min: 0, max: item.remaining, step: 0.1 }}
                              sx={{ width: 90, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, "& fieldset": { borderColor: hasErr ? "#ef4444" : "#e2e8f0" } } }} />
                          )}
                        </TableCell>
                      )}
                      {!isCompleted && (
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          {isDone ? (
                            <Typography fontSize={11} color="#cbd5e1">—</Typography>
                          ) : (
                            <TextField size="small" type="number" value={entry.damaged_qty || ""} placeholder="0"
                              onChange={e => updateEntry(item.product_id, "damaged_qty", Number(e.target.value))}
                              inputProps={{ min: 0, max: entry.received_qty, step: 0.1 }}
                              sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, "& fieldset": { borderColor: "#e2e8f0" } } }} />
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        {isDone ? (
                          <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Complete" size="small"
                            sx={{ height: 24, fontSize: 10, fontWeight: 700, bgcolor: "#f0fdf4", color: "#16a34a", "& .MuiChip-icon": { color: "#16a34a" } }} />
                        ) : item.already_received > 0 ? (
                          <Chip label="Partial" size="small" sx={{ height: 24, fontSize: 10, fontWeight: 700, bgcolor: "#fff7ed", color: "#ea580c" }} />
                        ) : (
                          <Chip label="Pending" size="small" sx={{ height: 24, fontSize: 10, fontWeight: 700, bgcolor: "#f1f5f9", color: "#94a3b8" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Bottom Submit */}
        {!isCompleted && (
          <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 3, border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 2 }}>
            <InventoryIcon sx={{ fontSize: 20, color: "#64748b" }} />
            <Typography fontSize={13} color="#64748b" sx={{ flex: 1 }}>
              Good quantities will be added to inventory. Damaged quantities will be recorded but not stocked.
            </Typography>
            <Button variant="contained" disableElevation onClick={handleSubmit} disabled={!hasAnyQty || hasErrors || saving}
              startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
              sx={{ borderRadius: 2, py: 1.25, px: 4, fontWeight: 700, fontSize: 14, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" }, textTransform: "none", "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
              {saving ? "Saving..." : "Confirm Receipt"}
            </Button>
          </Paper>
        )}

        {isCompleted && (
          <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 3, border: "1px solid #bbf7d0", bgcolor: "#f0fdf4", textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: "#16a34a", mb: 1 }} />
            <Typography fontSize={16} fontWeight={700} color="#16a34a">All items have been received</Typography>
            <Typography fontSize={13} color="#64748b" mt={0.5}>This purchase order is fully received</Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ReceiveGoods;
